import { UserImage } from "@circleco/ui-kit";
import { t } from "@/i18n-js/instance";
import { useDetermineConnectionStatus } from "@/react/components/shared/MembersList/CommunityMembers/hooks/useDetermineConnectionStatus";
import { usePunditUserContext } from "@/react/contexts";
import {
  dateFormats,
  formattedDateTime,
} from "@/react/helpers/dateTimeHelpers/formattedDateTime";
import { useSmScreenMediaQuery } from "@/react/hooks/useMediaQuery";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { useProfileContext } from "../../context";
import { ConnectionCTA } from "./ConnectionCTA";
import { ConnectionNote } from "./ConnectionNote";

const getBannerDescription = (member: any) => {
  const memberConnection = member?.connection_to_current_community_member;
  if (
    !memberConnection ||
    memberConnection.status === "detached" ||
    memberConnection.status === "ignored" ||
    memberConnection.status === "withdrawn"
  ) {
    return t("profile_drawer.connect_banner.description.default", {
      memberName: member.first_name,
    });
  }

  if (memberConnection?.status === "active") {
    return t("profile_drawer.connect_banner.description.connected", {
      date: formattedDateTime({
        dateTime: new Date(
          member.connection_to_current_community_member.created_at,
        ),
        format: dateFormats.long_date,
      }),
    });
  }

  if (memberConnection?.status === "pending") {
    // Connection was initiated by the other person
    if (memberConnection.initiator.id === member.id) {
      return t("profile_drawer.connect_banner.description.received", {
        memberName: member.first_name,
      });
    }

    return t("profile_drawer.connect_banner.description.sent", {
      memberName: member.first_name,
    });
  }

  return "";
};

export const ConnectBanner = ({
  recommendationId,
}: {
  recommendationId?: number;
}) => {
  const { member } = useProfileContext();
  const { currentCommunityMember } = usePunditUserContext();

  const isMobile = useSmScreenMediaQuery();

  const memberConnection = member.connection_to_current_community_member;

  const { isOtherUserSentConnection } =
    useDetermineConnectionStatus(memberConnection);

  return (
    <div className="border-primary mb-7 rounded-2xl border px-5 py-3 shadow-md">
      <div className="flex w-full flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
          <div className="flex items-center">
            <div className="size-9 overflow-hidden rounded-full">
              <UserImage
                size="9"
                name={member?.name ?? ""}
                src={member?.avatar_url}
              />
            </div>

            <div className="relative -ml-3 flex size-10 items-center justify-center overflow-hidden rounded-full bg-white">
              <div className="size-9 overflow-hidden rounded-full">
                <UserImage
                  size="9"
                  name={currentCommunityMember?.name ?? ""}
                  src={currentCommunityMember?.avatar_url}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-1 flex-col justify-center">
            <Typography.LabelSm weight="semibold">
              {t("profile_drawer.connect_banner.title")}
            </Typography.LabelSm>
            <Typography.BodyXs>
              {getBannerDescription(member)}
            </Typography.BodyXs>
          </div>
        </div>

        {isOtherUserSentConnection && memberConnection?.note && (
          <div className="flex md:hidden">
            <ConnectionNote
              note={memberConnection.note}
              lineClamp={2}
              collapse
              className="!m-0"
            />
          </div>
        )}

        <div className="w-fit">
          <ConnectionCTA
            member={member}
            shouldShowAcceptAtLeft={isMobile}
            recommendationId={recommendationId}
          />
        </div>
      </div>
      {isOtherUserSentConnection && memberConnection?.note && (
        <div className="hidden md:flex">
          <ConnectionNote note={memberConnection.note} lineClamp={1} collapse />
        </div>
      )}
    </div>
  );
};
